/** 
 * @fileoverview Componente Molecule para renderizar mensajes de ubicación en Inbox.
 * @module LocationMessageMC
 * @category Component - Molecule
 * @since 3.2.0
 * @version 3.2.0
 */
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import useLocationMessageData from '../../../hooks/inbox/data/useLocationMessageData';
import useLocationMessageUI from '../../../hooks/inbox/ui/useLocationMessageUI';
import MessageStatusMC from './MessageStatusMC';
import GroupSenderMC from './GroupSenderMC';
import LocationMC from './LocationMC';
import { useTraceUpdate } from '../../../hooks/shared/useDebugRenders';

/**
 * Renderiza la información de ubicación recibida en un mensaje:
 * - Muestra mapa, estado y remitente
 * - Permite guardar la dirección si es inbound
 * - Integra modal de gestión de direcciones
 *
 * @param {Object} props
 * @param {Object} props.conversation - Conversación actual (con plataforma)
 * @param {Object} props.message - Mensaje de ubicación
 *
 * @example
 * <LocationMessageMC conversation={conv} message={msg} />
 *
 * @see useLocationMessageData Para obtención de datos de ubicación
 * @see useLocationMessageUI Para estado UI del modal
 */
const LocationMessageMC = memo(({
  conversation,
  message
}) => {

  const { t } = useTranslation('InboxMC');
  const {
    latitude,
    longitude,
    inbound,
    locationURL,
    customerWebId
  } = useLocationMessageData(message, conversation);

  const {
    openCreateAddress
  } = useLocationMessageUI({
    latitude,
    longitude,
    customerWebId,
  });

  // 🚀 DEBUG RENDER - Rastrear re-renders en LocationMessageMC
  useTraceUpdate('LocationMessageMC', {
    conversationId: conversation?.id,
    messageId: message?.id,
    inbound,
    latitude,
    longitude
  }, {
    logRenderCount: true,
    logPropChanges: true,
  });

  return (
    <>
      <div className="tw-p-2 tw-rounded-t-[12px]">
        <GroupSenderMC message={message} />
        <LocationMC locationURL={locationURL} />
        <MessageStatusMC message={message} platform={conversation.platform} />
      </div>

      {inbound && (
        <div className="tw-flex tw-justify-center tw-p-3">
          <button
            type="button"
            className="tw-bg-white tw-border-none tw-rounded-xl tw-px-8 tw-py-2 tw-w-full"
            onClick={openCreateAddress}
          >
            <span className="tw-text-[#3CAAE1] tw-text-sm tw-font-normal">
              {t('location.save_address')}
            </span>
          </button>
        </div>
      )}
    </>
  );
});

LocationMessageMC.displayName = 'LocationMessageMC';

export default LocationMessageMC;
